import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Header, SpaceBetween, Box, Button, Table,
  Modal, FormField, DatePicker, Textarea, Badge,
} from "@cloudscape-design/components";
import { UserInfo, MeetingMinutes } from "../types";
import { apiClient } from "../api/client";

interface Props { user: UserInfo; siteId: string; }

export const CallMinutes: React.FC<Props> = ({ user, siteId }) => {
  const [minutes, setMinutes] = useState<MeetingMinutes[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MeetingMinutes | null>(null);
  const [newDate, setNewDate] = useState("");
  const [newText, setNewText] = useState("");
  const [editText, setEditText] = useState("");

  const isAdmin = user.role === "site-admin" || user.role === "superadmin";

  const fetchMinutes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/minutes?siteId=${siteId}`);
      setMinutes(data.minutes || []);
    } catch (err) {
      console.error("Failed to fetch minutes:", err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchMinutes(); }, [fetchMinutes]);

  const handleCreate = async () => {
    if (!newDate || !newText) return;
    try {
      await apiClient.post("/minutes", { siteId: siteId, date: newDate, text: newText });
      setShowAddModal(false);
      setNewDate("");
      setNewText("");
      fetchMinutes();
    } catch (err) {
      console.error("Failed to create minutes:", err);
    }
  };

  const handleEdit = (item: MeetingMinutes) => {
    setEditingItem(item);
    setEditText(item.text);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingItem || !editText) return;
    try {
      await apiClient.put(`/minutes/${editingItem.minuteId}`, {
        siteId: siteId,
        date: editingItem.date,
        text: editText,
      });
      setShowEditModal(false);
      setEditingItem(null);
      setEditText("");
      fetchMinutes();
    } catch (err) {
      console.error("Failed to update minutes:", err);
    }
  };

  const handleDelete = async (item: MeetingMinutes) => {
    if (!confirm("Delete these minutes?")) return;
    try {
      await apiClient.delete(`/minutes/${item.minuteId}`, { siteId: siteId, date: item.date });
      fetchMinutes();
    } catch (err) {
      console.error("Failed to delete minutes:", err);
    }
  };

  return (
    <SpaceBetween size="l">
      <Container header={
        <Header
          variant="h1"
          actions={isAdmin ? <Button variant="primary" onClick={() => setShowAddModal(true)}>Add Minutes</Button> : undefined}
        >
          Call Attendance / Minutes
        </Header>
      }>
        <Table
          loading={loading}
          items={minutes}
          columnDefinitions={[
            {
              id: "date",
              header: "Date",
              cell: (item) => new Date(item.date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
              width: 140,
              sortingField: "date",
            },
            {
              id: "text",
              header: "Minutes",
              cell: (item) => <Box variant="p"><pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>{item.text}</pre></Box>,
            },
            {
              id: "createdBy",
              header: "Added by",
              cell: (item) => item.createdBy,
              width: 120,
            },
            ...(isAdmin ? [{
              id: "actions",
              header: "Actions",
              cell: (item: MeetingMinutes) => (
                <SpaceBetween direction="horizontal" size="xxs">
                  <Button variant="icon" iconName="edit" onClick={() => handleEdit(item)} />
                  <Button variant="icon" iconName="remove" onClick={() => handleDelete(item)} />
                </SpaceBetween>
              ),
              width: 100,
            }] : []),
          ]}
          sortingDisabled
          empty="No meeting minutes recorded yet."
        />
      </Container>

      {/* Add Modal */}
      <Modal
        visible={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        header="Add Meeting Minutes"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate}>Save</Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Date">
            <DatePicker value={newDate} onChange={({ detail }) => setNewDate(detail.value)} placeholder="YYYY-MM-DD" />
          </FormField>
          <FormField label="Minutes / Notes">
            <Textarea value={newText} onChange={({ detail }) => setNewText(detail.value)} rows={8} placeholder="Attendees and discussion points..." />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Edit Modal */}
      <Modal
        visible={showEditModal}
        onDismiss={() => setShowEditModal(false)}
        header={`Edit Minutes — ${editingItem ? new Date(editingItem.date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}`}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleUpdate}>Update</Button>
          </SpaceBetween>
        }
      >
        <FormField label="Minutes / Notes">
          <Textarea value={editText} onChange={({ detail }) => setEditText(detail.value)} rows={8} />
        </FormField>
      </Modal>
    </SpaceBetween>
  );
};
